
import React, {useEffect, useMemo, useState} from 'react';
import {
  FilterPanelOption, 
  hasValue,
  useDebounce,
  VehicleFilterWithPaging,
  VehicleListings,
  VehicleWebViewFiltersWithPaging
} from "@amsanalytics/react-shared-library";
import {useSelector} from "react-redux";
import {AppState} from "../../../Store/rootReducer";
import {useAppDispatch} from "../../../Store/useAppDispatch";
import {
  getDownPaymentHistories,
  loadCuratedApplicationDetails,
  loadCuratedVehicles,
  setVehicleFilters,
} from "../../../Store/CuratedVehicles/actions";
import {useNavigate, useParams} from "react-router";
import {loadLibrary, setClient} from "../../../Store/Shared/actions";
import useWindowDimensions from "../../../Hooks/useWindowDimensions";
import useThirdPartyChatbot from "../../../Hooks/useThirdPartyChatbot";
import MessageViewer from "../../Controls/MessageViewer/MessageViewer";
import {encodeDepositPageParameters} from "./CuratedVehiclesUtils";
import {MS_PER_MINUTE} from "@progress/kendo-date-math";
import {GuidedTour} from "../../Controls/GuidedTour/GuidedTour";
import {getCuratedSiteTourSteps} from "./curatedSiteTourSteps";
import {useCuratedSiteTour} from "../../../Hooks/useCuratedSiteTour";

export interface CuratedVehiclesPageProps {
  headerAndFooterHeight: number;
}

export default function CuratedVehiclesPage (props:  CuratedVehiclesPageProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const clientId = (params as any)?.clientId;
  const customerPortalLinkId = (params as any)?.customerPortalLinkId;
  
  const loadingDetail = useSelector((state: AppState) => state.curatedVehicles.loading);
  const vehiclesLoaded = loadingDetail.vehiclesLoaded;
  const applicationDetailsLoaded = loadingDetail.applicationDetailsLoaded;
  const applicationDetails = useSelector((state: AppState) => state.curatedVehicles.application);
  const defaultFilters = applicationDetails?.defaultFilters ?? null; // Also represents the mins and maxes
  const selectedFilters = applicationDetails?.selectedFilters ?? null; // This is set and used in case the user navigates away and comes back.
  const isLoading = !vehiclesLoaded || !applicationDetailsLoaded;
  const pagedVehiclesWithLookups = useSelector((state: AppState) => state.curatedVehicles.vehicles);
  const downPaymentHistories = useSelector((state: AppState) => state.curatedVehicles.downPaymentHistories);

  useThirdPartyChatbot(applicationDetails?.chatbotScriptUrl);

  const { width, height } = useWindowDimensions() as any;
  const isMobile = width < 600;
  
  // Used for local, snappy updates
  const [localFiltersWithPaging, setLocalFiltersWithPaging] = useState<VehicleWebViewFiltersWithPaging | null>(selectedFilters);
  const debouncedFiltersWithPagingState = useDebounce(localFiltersWithPaging, 500); // Wait ___ms after user stops changing the filters

  // Dynamic max monthly payment: capped by the lesser of income-based max and down-payment-based max.
  // The server applies paymentPercentOfMonthlyIncome against monthly income to produce maxMonthlyPayment.
  // Here we apply the same percentage against the selected down payment as a secondary cap.
  const effectiveMaxMonthlyPayment = useMemo(() => {
    if (!applicationDetails || !applicationDetails.paymentPercentOfMonthlyIncome) {
      return applicationDetails?.maxMonthlyPayment ?? 0;
    }
    const selectedDownPayment = localFiltersWithPaging?.downPayment ?? applicationDetails.minDownPayment;
    const downPaymentBasedMax = applicationDetails.paymentPercentOfMonthlyIncome * selectedDownPayment;
    const cappedMax = Math.min(applicationDetails.maxMonthlyPayment, downPaymentBasedMax);
    return Math.max(applicationDetails.minMonthlyPayment, Math.round(cappedMax * 100) / 100);
  }, [applicationDetails, localFiltersWithPaging?.downPayment]);

  useEffect(() => {
    if (!localFiltersWithPaging){
      setLocalFiltersWithPaging(defaultFilters);
    }
  }, [defaultFilters]);

  // Clamp the monthly payment slider value when effectiveMaxMonthlyPayment drops below current selection
  useEffect(() => {
    if (localFiltersWithPaging && effectiveMaxMonthlyPayment > 0 && localFiltersWithPaging.monthlyPayment > effectiveMaxMonthlyPayment) {
      setLocalFiltersWithPaging({
        ...localFiltersWithPaging,
        monthlyPayment: effectiveMaxMonthlyPayment
      });
    }
  }, [effectiveMaxMonthlyPayment, localFiltersWithPaging?.monthlyPayment]);
  
  // Uses the debounced state of the filters before calling the update event
  useEffect(() => {
    if (debouncedFiltersWithPagingState) {
      dispatch(setVehicleFilters(debouncedFiltersWithPagingState)); // sets global state for when we navigate away
      fetchNewVehicles(debouncedFiltersWithPagingState); // calls the server
    }
  }, [debouncedFiltersWithPagingState]);

  useEffect(() => {
    if (hasValue(applicationDetails?.applicationId) && hasValue(clientId)) {
      // Once when it loads
      dispatch(getDownPaymentHistories(applicationDetails!.applicationId, clientId));

      // Repeats every X minutes
      let timer = setInterval(async () => {
        dispatch(getDownPaymentHistories(applicationDetails!.applicationId, clientId));
      }, 2 * MS_PER_MINUTE); // 2 Minutes
      return () => clearInterval(timer);
    }
  }, [applicationDetails?.applicationId, clientId]);

  // Redirect to the thank you page if they've already made a payment
  if (downPaymentHistories.length > 0 && customerPortalLinkId) {
    navigate(`/${clientId}/payment-thank-you/${customerPortalLinkId}`);
  }
  
  const onShowDetails = (vehicleId: string): void => {
    navigate(`/${clientId}/curated/${customerPortalLinkId}/${vehicleId}`);
  };

  const onMakeDownPayment = (vehicleId: string): void => {
    const vehicle = pagedVehiclesWithLookups.result.find(v => v.id === vehicleId);
    if (!vehicle || !applicationDetails || !selectedFilters) {
      return;
    }
    
    const encodedParams = encodeDepositPageParameters(
      customerPortalLinkId, 
      vehicle.stockNumber, 
      applicationDetails.applicationId, 
      applicationDetails.fixedDepositAmount ?? selectedFilters.downPayment
    );
    navigate(`/${clientId}/deposit/${encodedParams}`);
  };
  
  const setFiltersAndPaging = (filters: VehicleWebViewFiltersWithPaging) => {
    const pageChanged = filters.skip !== localFiltersWithPaging?.skip;
    setLocalFiltersWithPaging({
      ...filters,
      skip: pageChanged // If the page change, update the page. Otherwise, go to the first page
        ? filters.skip
        : 0
    });
  };
  
  const fetchNewVehicles = (filters: VehicleWebViewFiltersWithPaging) => {
    // Transforming the component's filters to what the server uses
    const vehicleFilterWithPaging: VehicleFilterWithPaging = {
      listOptions: {
        skip: filters.skip,
        take: filters.take,
        sort: filters?.sortBy
          ? [{
            dir: filters.sortBy.dir,
            field: filters.sortBy.field
          }]
          : []
      },
      yearStart: filters.yearRange.start,
      yearEnd: filters.yearRange.end,
      make: filters.make,
      model: filters.model,
      carOrTruck: filters.carOrTruck,
      minPrice: 0,
      maxPrice: filters.maxPrice,
      searchText: filters.searchText
    };
    dispatch(loadCuratedVehicles(clientId, vehicleFilterWithPaging));
  }

  useEffect(() => {
    // Load lookups if we have enough info.
    const clientIdFromParams = (params as any)?.clientId;
    if (clientIdFromParams) {
      if (clientId !== clientIdFromParams) {
        dispatch(setClient((params as any)?.clientId));
      }
      
      if (!applicationDetailsLoaded) {
        dispatch(loadCuratedApplicationDetails(clientIdFromParams, customerPortalLinkId));
      }
      dispatch(loadLibrary(clientId));
    }
  }, [params]);
  
  const defaultExpandedPanels: FilterPanelOption[] | undefined = isMobile
    ? ["downPayment", "monthlyPayment"]
    : undefined;

  // The tour waits for the config AND at least one vehicle card, so every step target exists
  // before Joyride measures; GuidedTour drops any step whose target is still absent.
  const tourReady = applicationDetailsLoaded
    && vehiclesLoaded
    && !!applicationDetails
    && !!localFiltersWithPaging
    && pagedVehiclesWithLookups.result.length > 0;
  const tourEnabled = applicationDetails?.enableGuidedTour ?? false;
  // No replay button on the page: the tour runs once per browser and the chatbot launcher owns
  // the bottom-right corner. useCuratedSiteTour still exposes startTour if a replay is ever wired up.
  const {runTour, tourKey, handleTourFinish} = useCuratedSiteTour({
    clientId,
    enabled: tourEnabled,
    ready: tourReady
  });
  const tourSteps = useMemo(() => getCuratedSiteTourSteps(
    applicationDetails?.guidedTourWelcomeMessage ?? '',
    applicationDetails?.makeDepositButtonText ?? 'Make Deposit'
  ), [applicationDetails?.guidedTourWelcomeMessage, applicationDetails?.makeDepositButtonText]);


  return (
    <>
      {applicationDetails && defaultFilters && localFiltersWithPaging && // Waiting only on the config because those values are crucial to the filters. It's fast.
        <VehicleListings
          pagedVehicles={pagedVehiclesWithLookups}
          isLoading={isLoading}
          onShowDetails={onShowDetails}
          onMakeDownPayment={onMakeDownPayment}
          showVehicleLength={applicationDetails.showVehicleLength}
          onFilterAndPagingUpdate={setFiltersAndPaging}
          filtersWithPaging={localFiltersWithPaging}
          overhead={applicationDetails.overhead}
          breakevenMonths={applicationDetails.breakevenMonths}
          carOrTruckOptions={pagedVehiclesWithLookups.carOrTrucks}
          makeOptions={pagedVehiclesWithLookups.makes}
          modelOptions={pagedVehiclesWithLookups.models}
          pageSizes={[5, 10, 20]}
          defaultFiltersWithPaging={defaultFilters}
          yearRange={defaultFilters.yearRange}
          monthlyPaymentRange={{start: applicationDetails.minMonthlyPayment, end: effectiveMaxMonthlyPayment}}
          downPaymentRange={{start: applicationDetails.minDownPayment, end: applicationDetails.maxDownPayment}}
          isMobile={isMobile}
          headerAndFooterHeight={props.headerAndFooterHeight}
          makeDepositButtonText={applicationDetails.makeDepositButtonText}
          defaultExpandedPanels={defaultExpandedPanels}
        />
      }
      {tourEnabled &&
        <GuidedTour steps={tourSteps} run={runTour} onFinish={handleTourFinish} tourKey={tourKey} />
      }
      <MessageViewer />
    </>
  );
}
